import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { AuthProvider } from "@/components/providers/auth-provider";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { PublicRoute } from "@/components/auth/public-route";
import { SessionTimeout } from "@/components/auth/session-timeout";

// Pages
import HomePage from "@/pages/page";
import FeaturesPage from "@/pages/features";
import { LoginPage } from "@/pages/auth/login";
import { SignupPage } from "@/pages/auth/signup";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password";
import { AuthCallbackPage } from "@/pages/auth/callback";
import { DashboardPage } from "@/pages/dashboard";
import { OnboardingPage } from "@/pages/onboarding";
import { LeadsPage } from "@/pages/leads";
import { TeamsPage } from "@/pages/teams";
import { CampaignsPage } from "@/pages/campaigns";
import { EmailTemplatesPage } from "@/pages/email-templates";
import { EmailSettingsPage } from "@/pages/email-settings";
import { FollowUpPage } from "@/pages/follow-up";
import { SettingsPage } from "@/pages/settings";
import { DiscoveryPage } from "@/pages/discovery";
import { NotFoundPage } from "@/pages/not-found";

// Layouts
import { AuthLayout } from "@/components/layouts/auth-layout";
import { DashboardLayout } from "@/components/layouts/dashboard-layout";

function App() {
  useEffect(() => {
    // Set up global error handlers
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      // In production, you might want to send this to an error tracking service
    };

    const handleError = (event: ErrorEvent) => {
      console.error("Global error:", event.error);
      // In production, you might want to send this to an error tracking service
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <ThemeProvider defaultTheme="light" storageKey="leadgpt-ui-theme">
      <AuthProvider>
        <Routes>
          {/* Public landing page */}
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />

          {/* Public routes */}
          <Route
            path="/auth/*"
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path="login" element={<LoginPage />} />
            <Route path="signup" element={<SignupPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="callback" element={<AuthCallbackPage />} />
            <Route path="*" element={<Navigate to="/auth/login" replace />} />
          </Route>

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="discovery" element={<DiscoveryPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="email-templates" element={<EmailTemplatesPage />} />
            <Route path="email-settings" element={<EmailSettingsPage />} />
            <Route path="follow-up" element={<FollowUpPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>
        </Routes>

        <SessionTimeout warningMinutes={5} timeoutMinutes={30} />
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
